import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Plus, Minus, Trash, ShoppingBag, WhatsappLogo, Gift, Sparkle, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DELIVERY_CHARGE, OFFER_TIERS, useCart } from '../lib/CartContext';
import { buildOrderRecord, formatOrderAsText } from '../lib/order';
import RewardMeter from './RewardMeter';
import OrderConfirmationModal from './OrderConfirmationModal';
import { getWhatsAppUrl } from './WhatsAppButton';

const buildCartWhatsAppMessage = ({ items, freeRewards, paidTotal, deliveryCharge, unlockedTierIds, orderId }) => {
  const lines = [];
  if (orderId) {
    lines.push(`Order ID: ${orderId}`);
    lines.push('');
  }
  lines.push('Hi Posterly! I would like to order:');
  lines.push('');

  items.forEach((it, i) => {
    lines.push(`${i + 1}. ${it.name}`);
    lines.push(`   Category: ${it.category}`);
    lines.push(`   Size: ${it.sizeLabel}`);
    lines.push(`   Qty: ${it.quantity}`);
    lines.push(`   Price: ₹${it.unitPrice} × ${it.quantity} = ₹${it.unitPrice * it.quantity}`);
    if (it.productId) lines.push(`   Link: ${window.location.origin}/product/${it.productId}`);
    lines.push('');
  });

  const freeEntries = Object.entries(freeRewards);
  if (freeEntries.length > 0) {
    lines.push('🎁 FREE ITEMS UNLOCKED:');
    freeEntries.forEach(([tierId, r], idx) => {
      const tier = OFFER_TIERS.find((t) => t.id === tierId);
      lines.push(`  ${idx + 1}. ${r.name}  (${tier ? tier.label : 'Free'})`);
      lines.push(`     Category: ${r.category} · Size: ${r.sizeLabel} · Price: FREE`);
      if (r.productId) lines.push(`     Link: ${window.location.origin}/product/${r.productId}`);
    });
    lines.push('');
  }

  const perks = [];
  if (unlockedTierIds.includes('free_shipping')) perks.push('Free Shipping');
  if (perks.length > 0) {
    lines.push(`Perks: ${perks.join(', ')}`);
    lines.push('');
  }

  lines.push('--------------------------');
  lines.push(`Subtotal: ₹${paidTotal}`);
  lines.push(`Delivery: ${deliveryCharge > 0 ? `₹${deliveryCharge}` : 'FREE'}`);
  lines.push(`Total: ₹${paidTotal + deliveryCharge}`);
  lines.push('');
  lines.push('Please confirm availability and share payment / delivery details.');
  return lines.join('\n');
};

const CartDrawer = () => {
  const {
    items,
    totalItems,
    paidTotal,
    deliveryCharge,
    grandTotal,
    isDrawerOpen,
    closeDrawer,
    updateQuantity,
    removeItem,
    freeRewards,
    unlockedTierIds,
    removeReward,
    openPicker,
  } = useCart();

  const [confirmedOrder, setConfirmedOrder] = useState(null);

  // Celebrate when a new tier is unlocked
  const prevUnlocked = useRef(unlockedTierIds);
  useEffect(() => {
    const previous = prevUnlocked.current;
    const newlyUnlocked = unlockedTierIds.filter((id) => !previous.includes(id));
    for (const tierId of newlyUnlocked) {
      const tier = OFFER_TIERS.find((t) => t.id === tierId);
      if (!tier) continue;
      toast.success(`🎉 ${tier.label} unlocked!`, { duration: 3500 });
      if (tier.needsPicker) {
        // Auto-prompt picker shortly after the celebration so it feels earned
        setTimeout(() => openPicker(tierId), 900);
      }
    }
    prevUnlocked.current = unlockedTierIds;
  }, [unlockedTierIds, openPicker]);

  const handleCopyOrder = async () => {
    if (items.length === 0) return;
    const order = buildOrderRecord({ items, freeRewards, paidTotal, unlockedTierIds });
    try {
      await navigator.clipboard.writeText(formatOrderAsText(order));
      toast.success('Order summary copied');
    } catch {
      toast.error('Could not copy — try again');
    }
  };

  const handleCheckout = () => {
    if (items.length === 0) return;
    const savedEmail =
      (typeof window !== 'undefined' &&
        window.localStorage.getItem('posterly_user_email')) ||
      null;
    const order = buildOrderRecord({
      items,
      freeRewards,
      paidTotal,
      deliveryCharge,
      unlockedTierIds,
      email: savedEmail,
    });
    const message = buildCartWhatsAppMessage({
      items,
      freeRewards,
      paidTotal,
      deliveryCharge,
      unlockedTierIds,
      orderId: order.id,
    });
    const url = getWhatsAppUrl((process.env.REACT_APP_WHATSAPP_NUMBER || '917416970039'), message);
    window.open(url, '_blank');

    // Not placed yet — the customer still has to press Send in WhatsApp.
    setConfirmedOrder({ order, whatsappUrl: url });
  };

  // Closing the popup keeps the cart so nothing is lost.
  const handleBackToCart = () => {
    setConfirmedOrder(null);
  };


  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            onClick={closeDrawer}
            data-testid="cart-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 260 }}
            className="fixed right-0 top-0 bottom-0 z-[61] w-full sm:w-[440px] bg-background border-l border-border/40 flex flex-col shadow-2xl"
            data-testid="cart-drawer"
            role="dialog"
            data-lenis-prevent
            aria-label="Shopping cart"
          >
            <header className="flex items-center justify-between px-6 py-5 border-b border-border/40 flex-shrink-0">
              <div className="flex items-center gap-3">
                <ShoppingBag size={22} weight="bold" className="text-foreground" />
                <h2 className="font-heading font-bold text-xl text-foreground">
                  Your Cart
                  {totalItems > 0 && (
                    <span className="ml-2 text-sm text-muted-foreground font-normal">
                      ({totalItems} {totalItems === 1 ? 'item' : 'items'})
                    </span>
                  )}
                </h2>
              </div>
              <button
                onClick={closeDrawer}
                className="text-muted-foreground hover:text-foreground p-1 rounded-sm transition-colors"
                aria-label="Close cart"
                data-testid="close-cart-button"
              >
                <X size={22} weight="bold" />
              </button>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-4" data-lenis-prevent>
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <ShoppingBag
                    size={56}
                    weight="thin"
                    className="text-muted-foreground/60 mb-4"
                  />
                  <p className="font-heading font-bold text-lg text-foreground mb-2">
                    Your cart is empty
                  </p>
                  <p className="text-sm text-muted-foreground max-w-[240px]">
                    Add some posters to unlock free shipping at ₹699+
                  </p>
                </div>
              ) : (
                <>
                  <RewardMeter compact />

                  <ul className="space-y-4" data-testid="cart-items-list">
                    {items.map((it) => (
                      <li
                        key={it.key}
                        className="flex gap-3 pb-4 border-b border-border/20 last:border-0"
                        data-testid={`cart-item-${it.key}`}
                      >
                        <div className="w-20 h-20 flex-shrink-0 bg-secondary border border-border/40 rounded-sm overflow-hidden">
                          <img
                            src={it.image_url}
                            alt={it.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm text-foreground line-clamp-2">
                              {it.name}
                            </p>
                            <button
                              onClick={() => removeItem(it.key)}
                              className="text-muted-foreground hover:text-red-500 p-1 -mr-1 transition-colors"
                              aria-label={`Remove ${it.name}`}
                              data-testid={`remove-item-${it.key}`}
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {it.category} · Size {it.sizeLabel}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1 border border-border/40 rounded-sm">
                              <button
                                onClick={() => updateQuantity(it.key, it.quantity - 1)}
                                className="p-1.5 hover:bg-muted transition-colors"
                                aria-label="Decrease quantity"
                                data-testid={`decrease-qty-${it.key}`}
                              >
                                <Minus size={12} weight="bold" />
                              </button>
                              <span
                                className="min-w-[24px] text-center text-sm font-medium"
                                data-testid={`qty-${it.key}`}
                              >
                                {it.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(it.key, it.quantity + 1)}
                                className="p-1.5 hover:bg-muted transition-colors"
                                aria-label="Increase quantity"
                                data-testid={`increase-qty-${it.key}`}
                              >
                                <Plus size={12} weight="bold" />
                              </button>
                            </div>
                            <p className="font-heading font-bold text-foreground">
                              ₹{it.unitPrice * it.quantity}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {Object.keys(freeRewards).length > 0 && (
                    <div className="pt-2" data-testid="free-rewards-list">
                      <p className="text-xs font-bold uppercase tracking-widest text-white/80 mb-2 flex items-center gap-1.5">
                        <Sparkle size={12} weight="fill" /> Your Free Rewards
                      </p>
                      <ul className="space-y-3">
                        {Object.entries(freeRewards).map(([tierId, r]) => {
                          const tier = OFFER_TIERS.find((t) => t.id === tierId);
                          return (
                            <li
                              key={tierId}
                              className="flex gap-3 items-center bg-secondary/70 border border-white/20 rounded-sm p-2"
                              data-testid={`reward-line-${tierId}`}
                            >
                              <div className="w-14 h-14 flex-shrink-0 bg-background border border-border/40 rounded-sm overflow-hidden">
                                <img
                                  src={r.image_url}
                                  alt={r.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-white line-clamp-1">
                                  {r.name}
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                  Size {r.sizeLabel} · {tier ? tier.label : 'Free'}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-heading font-black text-sm text-white leading-none">FREE</p>
                                <button
                                  type="button"
                                  onClick={() => removeReward(tierId)}
                                  className="text-[10px] text-muted-foreground hover:text-foreground underline mt-1"
                                >
                                  Change
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            {items.length > 0 && (
              <footer className="border-t border-border/40 px-6 py-5 space-y-3 bg-secondary/40 flex-shrink-0">
                <div className="space-y-1.5 text-sm" data-testid="cart-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground font-medium" data-testid="cart-subtotal">
                      ₹{paidTotal}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Delivery</span>
                    {deliveryCharge > 0 ? (
                      <span className="text-foreground font-medium" data-testid="cart-delivery">
                        ₹{deliveryCharge}
                      </span>
                    ) : (
                      <span className="text-green-400 font-bold flex items-center gap-1" data-testid="cart-delivery">
                        <Gift size={12} weight="fill" />
                        <span className="line-through text-muted-foreground font-normal mr-1">₹{DELIVERY_CHARGE}</span>
                        FREE
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-1.5 border-t border-border/30">
                    <span className="text-foreground font-semibold">Total</span>
                    <span
                      className="font-heading font-black text-2xl text-foreground"
                      data-testid="cart-total"
                    >
                      ₹{grandTotal}
                    </span>
                  </div>
                </div>
                <p
                  className="text-[11px] leading-snug text-muted-foreground bg-background/60 border border-border/30 rounded-sm px-3 py-2"
                  data-testid="delivery-note"
                >
                  🚚 Flat ₹{DELIVERY_CHARGE} delivery — same charge no matter how many posters you order.
                  {deliveryCharge > 0
                    ? ` Free delivery on orders above ₹${OFFER_TIERS[0].threshold}.`
                    : ' You unlocked free delivery!'}
                </p>
                <button
                  onClick={handleCheckout}
                  className="w-full bg-white text-black py-4 px-5 rounded-sm font-heading font-bold text-base flex items-center justify-center gap-2 hover:bg-white/90 transition-all shadow-md active:scale-[0.98]"
                  data-testid="checkout-whatsapp-button"
                >
                  <WhatsappLogo size={22} weight="fill" />
                  Checkout via WhatsApp
                </button>
                <button
                  onClick={handleCopyOrder}
                  className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                  data-testid="copy-order-button"
                >
                  <Copy size={12} weight="bold" />
                  Copy order summary
                </button>
                <p className="text-[11px] text-muted-foreground text-center">
                  You&apos;ll be redirected to WhatsApp with your full order details.
                </p>
              </footer>
            )}
          </motion.aside>
        </>
      )}
      {confirmedOrder && (
        <OrderConfirmationModal
          order={confirmedOrder.order}
          whatsappUrl={confirmedOrder.whatsappUrl}
          onBack={handleBackToCart}
        />
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
